import { THRESHOLDS, RAG_CARD_COLORS, RAG_DOT_COLORS, COMPLIANCE_RING_COLORS } from './dashboardConstants';

// ─── RAG Status Helpers ────────────────────────────────────────────────────

export function getRAGStatus(metric, value, data) {
  switch (metric) {
    case 'turnover': {
      if (value == null) return 'gray';
      return value <= THRESHOLDS.TURNOVER_RAG_GREEN ? 'green'
        : value <= THRESHOLDS.TURNOVER_RAG_AMBER ? 'amber' : 'red';
    }
    case 'compliance': {
      const total = value || 0;
      return total === 0 ? 'green'
        : total <= THRESHOLDS.MOM_COMPLIANCE_RAG_AMBER ? 'amber' : 'red';
    }
    case 'drc': {
      if (!value) return 'gray';
      return value <= THRESHOLDS.DRC_RAG_GREEN ? 'green'
        : value <= THRESHOLDS.DRC_RAG_AMBER ? 'amber' : 'red';
    }
    case 'passExpiry': {
      if (data == null) return 'gray';
      const urgent = value || 0;
      return urgent === 0 ? 'green'
        : urgent <= THRESHOLDS.PASS_EXPIRY_RAG_AMBER ? 'amber' : 'red';
    }
    case 'complianceExpiry': {
      if (data == null) return 'gray';
      const types = ['work_pass', 'passport', 'security_bond', 'safety_certs'];
      let worstLevel = 0; // 0=green, 1=amber, 2=red
      for (const key of types) {
        const count = data[key]?.action_required || 0;
        if (count > THRESHOLDS.PASS_EXPIRY_RAG_AMBER) { worstLevel = 2; break; }
        if (count > 0 && worstLevel < 1) worstLevel = 1;
      }
      return worstLevel === 0 ? 'green' : worstLevel === 1 ? 'amber' : 'red';
    }
    default:
      return 'gray';
  }
}

export function getRAGCardClass(status) {
  return RAG_CARD_COLORS[status] || RAG_CARD_COLORS.gray;
}

export function getRAGDotClass(status) {
  return RAG_DOT_COLORS[status] || RAG_DOT_COLORS.gray;
}

// ─── Turnover Badge / Interpretation Helpers ───────────────────────────────

export function getTurnoverBadge(type, rate) {
  const thresholds = type === 'staff'
    ? { healthy: THRESHOLDS.TURNOVER_STAFF_HEALTHY, monitor: THRESHOLDS.TURNOVER_STAFF_MONITOR }
    : { healthy: THRESHOLDS.TURNOVER_WORKER_HEALTHY, monitor: THRESHOLDS.TURNOVER_WORKER_MONITOR };

  if (rate <= thresholds.healthy) {
    return { level: 'healthy', bg: 'bg-green-100', text: 'text-green-700', label: 'Healthy' };
  } else if (rate <= thresholds.monitor) {
    return { level: 'monitor', bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Monitor' };
  }
  return { level: 'critical', bg: 'bg-red-100', text: 'text-red-700', label: 'Critical' };
}

export function getTurnoverInterpretation(type, rate) {
  const thresholds = type === 'staff'
    ? { healthy: THRESHOLDS.TURNOVER_STAFF_HEALTHY, monitor: THRESHOLDS.TURNOVER_STAFF_MONITOR }
    : { healthy: THRESHOLDS.TURNOVER_WORKER_HEALTHY, monitor: THRESHOLDS.TURNOVER_WORKER_MONITOR };

  const benchmarkRange = type === 'staff' ? '10-14%' : '15-20%';
  const label = type === 'staff' ? 'Staff' : 'Worker';

  if (rate <= thresholds.healthy) {
    return {
      level: 'healthy',
      color: 'text-green-600',
      message: `${label} retention healthy`,
      detail: `at ${rate.toFixed(1)}% (within Singapore construction range ${benchmarkRange})`,
    };
  } else if (rate <= thresholds.monitor) {
    return {
      level: 'monitor',
      color: 'text-yellow-600',
      message: `${label} turnover elevated`,
      detail: `at ${rate.toFixed(1)}% — exceeds Singapore construction range (${benchmarkRange})`,
    };
  }
  return {
    level: 'critical',
    color: 'text-red-600',
    message: `${label} turnover critical`,
    detail: `at ${rate.toFixed(1)}% — ${type === 'staff' ? 'immediate attention recommended' : 'significantly exceeds benchmark'}`,
  };
}

// ─── OT Utilization Helpers ────────────────────────────────────────────────

export function getOTGaugeStatus(pct) {
  if (pct <= THRESHOLDS.OT_GAUGE_MODERATE) {
    return { barColor: 'bg-emerald-500', textColor: 'text-emerald-700', label: 'Healthy — well within limits' };
  } else if (pct <= THRESHOLDS.OT_GAUGE_CRITICAL) {
    return { barColor: 'bg-amber-500', textColor: 'text-amber-700', label: 'Moderate — monitor closely' };
  }
  return { barColor: 'bg-red-500', textColor: 'text-red-700', label: 'Critical — approaching/exceeding limit' };
}

// ─── Compliance Summary Helpers ────────────────────────────────────────────

export function getComplianceSummary(counts, type) {
  const parts = Object.entries(counts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${v} ${k}`);
  return parts.length > 0 ? parts.join(' · ') : 'All clear';
}

// ─── Rest Day Compliance Ring ──────────────────────────────────────────────

export function getComplianceRingColor(rate) {
  if (rate >= THRESHOLDS.REST_DAY_COMPLIANCE_GREEN) return COMPLIANCE_RING_COLORS.green;
  if (rate >= THRESHOLDS.REST_DAY_COMPLIANCE_AMBER) return COMPLIANCE_RING_COLORS.amber;
  return COMPLIANCE_RING_COLORS.red;
}

// ─── RAG Icon Mapping (WCAG 1.4.1 — not color-only) ───────────────────────
// Returns icon name string; caller maps to actual component.
export function getRAGIconName(status) {
  switch (status) {
    case 'green': return 'check';
    case 'amber': return 'minus';
    case 'red': return 'alert';
    default: return 'loader';
  }
}

// ─── Alert Severity ────────────────────────────────────────────────────────

export function getAlertSeverityStyle(severity) {
  if (severity === 'critical') return { dot: 'bg-red-500', badge: 'bg-red-100 text-red-800', label: 'Critical' };
  if (severity === 'warning') return { dot: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800', label: 'Warning' };
  return { dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800', label: 'Monitor' };
}
